import { createSelector } from 'reselect';
import * as fromFlamingo from './flamingo.reducer';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { RecordsResult, DomainResult } from '@mode-switch/query';

export const getSignatures = createSelector(
  (state: fromFlamingo.FlamingoRoot) => state.flamingo.signatures,
  (signatures) => signatures
);

export const getCompletedPlanRefs = createSelector(
  (state: fromFlamingo.FlamingoRoot) => state.flamingo.completedPlanRefs,
  (refs) => refs
);

export function createReadySelector(planId: string) {
  return createSelector(
    (state: fromFlamingo.FlamingoRoot) => state.plan.states[planId],
    (state) => state === FlamingoTypes.PlanStatus.Succeeded
  );
}

export function createLoadingSelector(planId: string) {
  return createSelector(
    (state: fromFlamingo.FlamingoRoot) => state.plan.states[planId],
    (state) => state === FlamingoTypes.PlanStatus.Loading
  );
}

export function createInitSelector(planId: string) {
  return createSelector(
    (state: fromFlamingo.FlamingoRoot) => state.plan.states[planId],
    (state) => state === FlamingoTypes.PlanStatus.Init
  );
}

export function createPageLoadingSelector(planId: string) {
  return createSelector(
    (state: fromFlamingo.FlamingoRoot) => state.plan.states[planId],
    (state) => state === FlamingoTypes.PlanStatus.LoadingPage
  );
}

export function createFailedSelector(planId: string) {
  return createSelector(
    (state: fromFlamingo.FlamingoRoot) => state.plan.states[planId],
    (state) => state === FlamingoTypes.PlanStatus.Failed
  );
}

export function createPlanIdSelector(refId: string) {
  return function createPlanIdSelector(state: fromFlamingo.FlamingoRoot) {
    return state.flamingo.planRefs[refId];
  };
}

export const columnsSelector = createSelector(
  (state: fromFlamingo.FlamingoRoot) => state.plan.columnsByRef,
  (columns) => columns
);

export function createColumnsSelector(refId: string) {
  return (state: fromFlamingo.FlamingoRoot) => state.plan.columnsByRef[refId];
}

export function createDataProfilingSelector(token: string) {
  return (state: fromFlamingo.FlamingoRoot) => state.plan.dataProfiling[token];
}

export function createBinSizeToFieldMapSelector(token: string) {
  return (state: fromFlamingo.FlamingoRoot) => state.plan.binSizeToFieldMap[token];
}

export function createConditionalFormattingSelector(token: string) {
  return (state: fromFlamingo.FlamingoRoot) => state.plan.conditionalFormatting[token];
}

export function createPlanSelector(planId: string) {
  return function createPlanSelector(state: fromFlamingo.FlamingoRoot) {
    return state.plan.requests[planId];
  };
}

export function getPlanFromRefId(refId: string) {
  return (state: fromFlamingo.FlamingoRoot) => state.plan.requests[state.flamingo.planRefs[refId]];
}

export function createResultSelector(planId: string) {
  return (state: fromFlamingo.FlamingoRoot) => state.plan.results[planId];
}

export function createErrorsSelector(planId: string) {
  return (state: fromFlamingo.FlamingoRoot) => state.plan.errors[planId];
}

export function createCountSelector(planId: string) {
  return createSelector(createPlanSelector(planId), createResultSelector(planId), (request, result) => {
    if (request && result) {
      return RecordsResult.countValue(request as FlamingoTypes.ModeRecordsPlan, result);
    }

    return undefined;
  });
}

// Filter Domains
export function createDomainRecordsSelector(planId: string) {
  return createSelector(createPlanSelector(planId), createResultSelector(planId), createDomainRecordsSelectorFn);
}

export function createDomainRecordsSelectorFn(
  request: Readonly<FlamingoTypes.ModeSelectPlan>,
  result: Readonly<FlamingoTypes.ModeSelectResult>
): FlamingoTypes.DomainHash | undefined {
  if (request && result) {
    if ('parts' in request) {
      const results: FlamingoTypes.DomainHash = {};
      for (const part of request.parts) {
        const domain = DomainResult.get(request, result, part.field);
        if (domain != null) {
          results[part.field.alias] = domain;
        }
      }

      return results;
    }
  }

  return undefined;
}

export function createRecordsSelector(planId: string) {
  return createSelector(createPlanSelector(planId), createResultSelector(planId), (request, result) => {
    if (request && result) {
      const recordsResult = RecordsResult.recordsInline(request as FlamingoTypes.ModeRecordsPlan, result);
      if (recordsResult != null) {
        return {
          columns: recordsResult.columns,
          content: recordsResult.items,
        };
      }
    }

    return undefined;
  });
}

export function createStateSelector(planId: string) {
  return function createStateSelector(state: fromFlamingo.FlamingoRoot) {
    return state.plan.states[planId];
  };
}

export function createExportSelector(planId: string) {
  return (state: fromFlamingo.FlamingoRoot) => state.plan.exports[planId];
}
